import { put, takeLatest, select } from 'redux-saga/effects'
import TrackerService from '../../../services/Tracker'
import ListMetaData from '../../../modules/utilities/list/ListMetaData'
import FilterExpression from '../../../modules/utilities/list/FilterExpression'
import DeviceDataStoreSlice from '../../slices/data/DeviceDataStoreSlice'
import ErrorActions from '../../actions/Error'
import OLError from '../../../modules/errorHandling/models/OLError'
import TrackerManagementExperienceActions from '../../actions/experiences/TrackerManagement'
import TRACKER_MANAGEMENT_EXPERIENCE_TYPES from '../../types/experiences/TrackerManagement'
import LayoutActions from '../../actions/Layout'
import TrackerManagementTrackerListSlice from '../../slices/experiences/TrackerManagementTrackerListSlice'
import MessageActions from '../../actions/Messages'
import { AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT, DISPLAYED_MESSAGE_TYPES, IS_DEV_ENV } from '../../../constants/general'
import TrackerManagementTrackerPositionsSlice from '../../slices/experiences/TrackerManagementTrackerPositionsSlice'
import DeviceCoordinateService from '../../../services/DeviceCoordinate'
import DeviceCoordinateDataStoreSlice from '../../slices/data/DeviceCoordinateDataStoreSlice'
import TrackerManagementTrackerListExportSlice from '../../slices/experiences/TrackerManagementTrackerListExportSlice'
import TrackerManagementTrackerPositionsExportSlice
  from '../../slices/experiences/TrackerManagementTrackerPositionsExportSlice'
import TrackerManagementTrackerActivityLogsSlice from '../../slices/experiences/TrackerManagementTrackerActivityLogsSlice'
import TrackerActivityLogService from '../../../services/TrackerActivityLog'
import FETCH_IDS from '../../fetchIds'
import DeviceDataStoreSelectors from '../../selectors/data/Devices'
import StoreDevice from '../../models/Device'
import DeviceModelDataStoreSelectors from '../../selectors/data/DeviceModels'
import StoreDeviceModel from '../../models/DeviceModel'
import StoreDeviceCoordinate from '../../models/DeviceCoordinate'
import DataStoresActions from '../../actions/data'
import RESTModelNormalizationSchemas from '../../models/RESTModelNormalizationSchemas'
import { getApiErrorDisplayedMessage } from '../../../modules/trackers/trackerCommandPrerequisites'
import { isDeviceRestStoreId } from '../../../modules/chat/resolveDeviceChatIds'

function * fetchTrackers (action) {
  const sliceActions = action.type === TrackerManagementTrackerListExportSlice.types.FETCH
    ? TrackerManagementExperienceActions.listExport
    : TrackerManagementExperienceActions.list
  try {
    const { data: { limit, page, sort, filters = [], filtersLinkingType } } = action
    yield put(sliceActions.setIsLoading(true))

    const filterArgs = Array.isArray(filters) ? filters : []
    const filter = filterArgs.length > 0 ? new FilterExpression(filtersLinkingType, ...filterArgs) : null

    const result = yield TrackerService.FindMany(
      new ListMetaData(limit, page, sort, filter)
    )
    yield put(
      DeviceDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          yield put(
            sliceActions.setData(storeValues.result, storeValues.totalNrOfItems, false)
          )
        }
      )
    )
  } catch (e) {
    yield put(sliceActions.setIsLoading(false))
    if (IS_DEV_ENV) {
      console.debug('[TrackerManagementExperience->fetchTrackers] Error details:', {
        message: e?.message,
        status: e?.response?.status,
        url: e?.config?.url
      })
    }
    yield put(ErrorActions.trigger(
      new OLError(
        '[TrackerManagementExperience->fetchTrackers]: Failed to fetch trackers',
        `Failed to fetch trackers${e?.response?.status ? ` (${e.response.status})` : ''}`,
        e
      ),
      true,
      true,
      true,
      false
    ))
  }
}

function * createTracker (action) {
  const fetchId = 'TrackerManagementExperienceSaga.createTracker'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerItem, onSuccess } } = action

    const result = yield TrackerService.Create(trackerItem.toNormalizedRESTModel())
    if (result.status === 200 || result.status === 201) {
      // Persist full tracker (inputs/outputs/attrs) into data stores from the create response.
      yield hydrateTrackerFromResponse(result)
      if (result.data?.id) {
        yield prependTrackerToListSlice(result.data.id)
      }
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['CREATE_TRACKER_SUCCESS', 'Tracker successfully created'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[TrackerManagementExperience->createTracker]: Failed to create tracker', 'Failed to create tracker', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchTrackerDetails (action) {
  const fetchId = 'TrackerManagementExperienceSaga.fetchTrackerDetails'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId } } = action
    if (!isDeviceRestStoreId(trackerId)) {
      yield put(LayoutActions.setIsLoading(false, fetchId))
      return
    }

    const result = yield TrackerService.GetTrackerInfo(trackerId)
    yield hydrateTrackerFromResponse(result)
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->fetchTrackerDetails]: Failed to fetch tracker details',
          'Failed to load tracker details',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * editTracker (action) {
  const fetchId = 'TrackerManagementExperienceSaga.editTracker'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerItem, onSuccess } } = action

    const result = yield TrackerService.Update(trackerItem.id, trackerItem.toTrackerUpdateRESTModel())
    if (result.status === 200) {
      // Replace device + nested inputs/outputs/attrs with the PUT response so the edit form
      // and list reflect the latest predefined-event mappings immediately.
      yield hydrateTrackerFromResponse(result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['EDIT_TRACKER_SUCCESS', 'Tracker successfully updated'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
      onSuccess && onSuccess()
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[TrackerManagementExperience->editTracker]: Failed to update tracker', 'Failed to update tracker', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

/**
 * Normalize a single full tracker API payload into device + nested entity stores
 * (inputs, outputs, attribute values, coordinates), same path as fetchTrackerDetails.
 */
function * hydrateTrackerFromResponse (result) {
  if (!result?.data?.id) {
    return
  }
  yield put(
    DeviceDataStoreSlice.actions.fetchResult({
      data: { totalCount: 1, items: [result.data] }
    })
  )
}

function * prependTrackerToListSlice (trackerId) {
  const listState = yield select(state => state.experiences.trackerManagement.list)
  const currentIds = Array.isArray(listState?.data) ? listState.data : []
  const id = String(trackerId)
  if (currentIds.some(itemId => String(itemId) === id)) {
    return
  }
  yield put(
    TrackerManagementExperienceActions.list.setData(
      [id, ...currentIds],
      (listState?.totalNumberOfItems || 0) + 1,
      false
    )
  )
}

function * removeTrackerFromListSlice (trackerId) {
  const listState = yield select(state => state.experiences.trackerManagement.list)
  const currentIds = Array.isArray(listState?.data) ? listState.data : []
  const id = String(trackerId)
  const nextIds = currentIds.filter(itemId => String(itemId) !== id)
  if (nextIds.length !== currentIds.length) {
    const total = Math.max(0, (listState?.totalNumberOfItems || 0) - 1)
    yield put(TrackerManagementExperienceActions.list.setData(nextIds, total, false))
  }
}

/** Soft-delete response is message-only — patch the cached device to match server lifecycle. */
function * markTrackerDeletedInStore (trackerId) {
  const devices = yield select(DeviceDataStoreSelectors.devices)
  const existing = devices?.[String(trackerId)]
  if (!existing) {
    return
  }
  yield put(
    DataStoresActions.mergeEntitiesIntoStores({
      [RESTModelNormalizationSchemas.device.entityName]: {
        [String(trackerId)]: {
          ...existing,
          isDeleted: true,
          enabled: false,
          isActive: false,
          // Seed parity with DeviceTrackerService.DeleteAsync
          statusId: 5,
          statusChangeCodeId: 8
        }
      }
    })
  )
}

function * deleteTracker (action) {
  const fetchId = 'TrackerManagementExperienceSaga.deleteTracker'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId } } = action

    const result = yield TrackerService.Delete(trackerId)
    if (result.status === 200) {
      yield markTrackerDeletedInStore(trackerId)
      yield removeTrackerFromListSlice(trackerId)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['DELETE_TRACKER_SUCCESS', 'Tracker deleted (soft).'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError('[TrackerManagementExperience->deleteTracker]: Failed to delete tracker', 'Failed to delete tracker', e),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * sendOutputToTracker (action) {
  const fetchId = 'TrackerManagementExperienceSaga.sendOutputToTracker'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId, trackerOutputId } } = action

    const result = yield TrackerService.SendOutput(trackerId, trackerOutputId)
    if (result.status === 200) {
      const channel = result.data?.deliveryChannel
      const viaLabel = channel === 'sms' ? 'SMS' : channel === 'data' ? 'data' : null
      const successMessage = viaLabel
        ? ['TRACKER_OUTPUT_SUCCESS_VIA', `Output successfully sent via ${viaLabel}`]
        : ['TRACKER_OUTPUT_SUCCESS', 'Output successfully sent']
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          successMessage,
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    const displayedMessage = getApiErrorDisplayedMessage(
      e,
      'Failed to send output to the tracker'
    )
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->sendOutputToTracker]: Failed to send output to tracker',
          displayedMessage,
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * listenTracker (action) {
  const fetchId = FETCH_IDS.TRACKER_MANAGEMENT_EXPERIENCE?.LISTEN_TRACKER || 'TrackerManagementExperienceSaga.listenTracker'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId, listeningPhoneNumber } } = action

    const result = yield TrackerService.Listen(trackerId, listeningPhoneNumber)
    if (result.status === 200) {
      const channel = result.data?.deliveryChannel
      const viaLabel = channel === 'sms' ? 'SMS' : channel === 'data' ? 'data' : null
      const successMessage = viaLabel
        ? ['TRACKER_LISTEN_SUCCESS_VIA', `Listening successfully started via ${viaLabel}`]
        : ['TRACKER_LISTEN_SUCCESS', 'Listening successfully started']
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          successMessage,
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    const displayedMessage = getApiErrorDisplayedMessage(
      e,
      'Failed to listen to the tracker'
    )
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->listenTracker]: Failed to initiate listening to the tracker',
          displayedMessage,
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * updateActiveAttribute (action) {
  const fetchId = 'TrackerManagementExperienceSaga.updateActiveAttribute'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId, isActive } } = action

    const result = yield TrackerService.UpdateIsActiveAttribute(trackerId, isActive)
    if (result.status === 200) {
      yield hydrateTrackerFromResponse(result)
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['TRACKER_ACTIVE_ATTRIBUTE_UPDATE_SUCCESS', 'Active state was successfully changed'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->updateActiveAttribute]: Changing the active state has failed',
          'Failed to change the tracker\'s active state',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * detachUser (action) {
  const fetchId = 'TrackerManagementExperienceSaga.detachUser'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId } } = action

    const result = yield TrackerService.DetachUser(trackerId)
    if (result.status === 200) {
      // display success message
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          ['TRACKER_DETACH_USER_SUCCESS', 'Tracker user successfully detached'],
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->detachUser]: Failed to detach the tracker from the user',
          'Failed to detach the tracker from the user',
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchTrackerPositions (action) {
  const sliceActions = action.type === TrackerManagementTrackerPositionsExportSlice.types.FETCH
    ? TrackerManagementExperienceActions.trackerPositionsExport
    : TrackerManagementExperienceActions.trackerPositions
  try {
    const { data: { limit, page, sort, filters = [], filtersLinkingType } } = action
    yield put(sliceActions.setIsLoading(true))

    // Extract deviceId from filters and remove it from the filters array
    // since it will be used in the URL path instead
    const filtersList = Array.isArray(filters) ? filters : []
    const deviceIdFilter = filtersList.find(f => f.attributeName === 'deviceId')
    const deviceId = deviceIdFilter?.value
    const filtersWithoutDeviceId = filtersList.filter(f => f.attributeName !== 'deviceId')

    const filterArgsWithDeviceId = Array.isArray(filtersWithoutDeviceId) ? filtersWithoutDeviceId : []
    const filterArgsAll = Array.isArray(filters) ? filters : []
    const filterPositions = filterArgsWithDeviceId.length > 0 ? new FilterExpression(filtersLinkingType, ...filterArgsWithDeviceId) : null
    const filterPositionsFallback = filterArgsAll.length > 0 ? new FilterExpression(filtersLinkingType, ...filterArgsAll) : null

    // Use the device-specific endpoint if deviceId is available
    const result = deviceId
      ? yield DeviceCoordinateService.FindManyByDeviceId(
        deviceId,
        new ListMetaData(limit, page, sort, filterPositions)
      )
      : yield DeviceCoordinateService.FindMany(
        new ListMetaData(limit, page, sort, filterPositionsFallback)
      )

    yield put(
      DeviceCoordinateDataStoreSlice.actions.fetchResult(
        result,
        function * (storeValues) {
          yield put(
            sliceActions.setData(storeValues.result, storeValues.totalNrOfItems, false)
          )
        }
      )
    )
  } catch (e) {
    yield put(sliceActions.setIsLoading(false))
    yield put(ErrorActions.trigger(
      new OLError('[TrackerManagementExperience->fetchTrackerPositions]: Failed to fetch tracker positions', 'Failed to fetch tracker positions', e),
      true,
      true,
      true,
      false
    ))
  }
}

function * sendCommand (action) {
  const fetchId = 'TrackerManagementExperienceSaga.sendCommand'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { trackerId, command } } = action
    const devices = yield select(DeviceDataStoreSelectors.devices)
    const deviceModels = yield select(DeviceModelDataStoreSelectors.deviceModels)
    const tracker = StoreDevice.FromJSON(devices[trackerId])
    const deviceModel = StoreDeviceModel.FromJSON(deviceModels[tracker.modelId])

    const result = yield TrackerService.SendCommand(trackerId, `${deviceModel.smsCommandPrefix}${command}`)
    if (result.status === 200) {
      const channel = result.data?.deliveryChannel
      const viaLabel = channel === 'sms' ? 'SMS' : channel === 'data' ? 'data' : null
      const successMessage = viaLabel
        ? ['TRACKER_SEND_COMMAND_SUCCESS_VIA', `Command successfully sent via ${viaLabel}`]
        : ['TRACKER_SEND_COMMAND_SUCCESS', 'Command successfully sent']
      yield put(
        MessageActions.add(
          DISPLAYED_MESSAGE_TYPES.SUCCESS,
          undefined,
          successMessage,
          AUTO_HIDE_MESSAGE_AFTER_SEC_DEFAULT
        )
      )
    }
  } catch (e) {
    const displayedMessage = getApiErrorDisplayedMessage(
      e,
      'Failed to send command to tracker'
    )
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->sendCommand]: Failed to send command',
          displayedMessage,
          e
        ),
        true,
        true,
        true,
        false
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchLocationStringForCoordinate (action) {
  const fetchId = 'TrackerManagementExperienceSaga.fetchLocationStringForCoordinate'
  try {
    yield put(LayoutActions.setIsLoading(true, fetchId))

    const { data: { deviceCoordinateId } } = action

    const result = yield DeviceCoordinateService.GetLocationStringForCoordinate(deviceCoordinateId)
    if (result.status === 200) {
      const deviceCoordinate = StoreDeviceCoordinate.FromNormalizedRESTModel(result.data)
      // merge the fetched device coordinate data into the device coordinates store
      yield put(
        DataStoresActions.mergeEntitiesIntoStores({
          [RESTModelNormalizationSchemas.deviceCoordinate.entityName]: ({
            [deviceCoordinate.id]: deviceCoordinate
          })
        })
      )
    }
  } catch (e) {
    yield put(
      ErrorActions.trigger(
        new OLError(
          '[TrackerManagementExperience->fetchLocationStringForCoordinate]: Failed to fetch location string for device coordinate',
          'Failed to fetch location string for device coordinate',
          e
        ),
        true,
        true,
        true,
        true
      )
    )
  }
  yield put(LayoutActions.setIsLoading(false, fetchId))
}

function * fetchTrackerActivityLogs (action) {
  try {
    const { data: { limit, page, sort, filters = [] } } = action
    const trackerId = filters.find(f => f.attributeName === 'trackerId')?.value

    yield put(TrackerManagementExperienceActions.activityLogs.setIsLoading(true))

    if (!trackerId) {
      yield put(TrackerManagementExperienceActions.activityLogs.setData([], 0, false))
      return
    }

    const result = yield TrackerActivityLogService.FindManyByTrackerId(
      trackerId,
      new ListMetaData(limit, page, sort)
    )

    yield put(
      TrackerManagementExperienceActions.activityLogs.setData(
        result.data.items || [],
        result.data.totalCount || 0,
        false
      )
    )
  } catch (e) {
    yield put(TrackerManagementExperienceActions.activityLogs.setIsLoading(false))
    yield put(ErrorActions.trigger(
      new OLError(
        '[TrackerManagementExperience->fetchTrackerActivityLogs]: Failed to fetch tracker activity logs',
        'Failed to fetch tracker activity logs',
        e
      ),
      true,
      true,
      true,
      false
    ))
  }
}

function * TrackerManagementExperienceSaga () {
  yield takeLatest(TrackerManagementTrackerListSlice.types.FETCH, fetchTrackers)
  yield takeLatest(TrackerManagementTrackerListExportSlice.types.FETCH, fetchTrackers)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.CREATE, createTracker)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.FETCH_DETAILS, fetchTrackerDetails)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.EDIT, editTracker)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.DELETE, deleteTracker)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.SEND_OUTPUT_TO_TRACKER, sendOutputToTracker)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.LISTEN, listenTracker)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.UPDATE_ATTRIBUTE_ACTIVE, updateActiveAttribute)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.DETACH_USER, detachUser)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.SEND_COMMAND, sendCommand)
  yield takeLatest(TRACKER_MANAGEMENT_EXPERIENCE_TYPES.FETCH_LOCATION_STRING_FOR_COORDINATE, fetchLocationStringForCoordinate)
  yield takeLatest(TrackerManagementTrackerPositionsSlice.types.FETCH, fetchTrackerPositions)
  yield takeLatest(TrackerManagementTrackerPositionsExportSlice.types.FETCH, fetchTrackerPositions)
  yield takeLatest(TrackerManagementTrackerActivityLogsSlice.types.FETCH, fetchTrackerActivityLogs)
}

export default TrackerManagementExperienceSaga
